import React from 'react'
import { makeStyles } from '@material-ui/core'
import type { ActivityProps, StyleClasses } from '@paintscout/ui'
import { Activity, Link, docTypeSwitch } from '@paintscout/ui'
import qs from 'query-string'
import { useLocation } from 'react-router-dom'

const useStyles = makeStyles((theme) => ({
  link: {
    color: theme.palette.text.primary,
    textDecoration: 'none',
    '&:visited, &:active, &:focus': {
      color: theme.palette.text.primary
    }
  }
}))

export interface ActivityLinkProps extends ActivityProps {
  classes?: ActivityProps['classes'] & StyleClasses<typeof useStyles>
}

export default function ActivityLink(props: ActivityLinkProps) {
  const { link: linkClass, ...classes } = useStyles(props)
  const location = useLocation()

  function handleLinkClick(e: React.MouseEvent) {
    if ((e.target as HTMLElement).closest('.no-redirect')) {
      e.preventDefault()
    }
  }

  // const noteActivityTypes = ['note'] // Note activity links handled in NoteActivity component
  const eventActivityTypes = [
    'event',
    'event-created',
    'event-cancelled',
    'event-assigned',
    'event-email-sent',
    'event-progress-updated'
  ]
  const taskActivityTypes = ['task-created', 'task-assigned', 'task-completed']
  const dealActivityTypes = ['deal-created', 'deal-assigned', 'deal-stage-changed', 'lead-form-submission']
  const fileActivityTypes = ['file-added']

  let link = ''
  const { activity } = props
  const { details } = activity || {}
  const { activityType, docType } = details || {}

  if (docType === 'quote') {
    const id = details.quote?.id || details.invoice?.id
    const isInvoice = !!details.invoice
    link = `/${isInvoice ? 'invoices' : 'quotes'}/edit/${id}`

    if (activityType === 'message') {
      link += '?' + qs.stringify({ message: activity._id })
    }

    if (activityType === 'sent' && (details as any).isWorkOrder) {
      link += '?view=work-order'
    }
  } else if (eventActivityTypes.includes(activityType)) {
    link = details?.deal?.id ? `/deals/edit/${details.deal.id}?eventTab=events&viewTab=details` : null
  } else if (taskActivityTypes.includes(activityType)) {
    link = details?.deal?.id ? `/deals/edit/${details.deal.id}?eventTab=tasks&viewTab=details` : null
  } else if (dealActivityTypes.includes(activityType)) {
    link = details?.deal?.id ? `/deals/edit/${details.deal.id}` : null
  } else if (fileActivityTypes.includes(activityType)) {
    link = activity?.docIds?.[0] ? docTypeSwitch({ eventType: 'files', docType, id: activity.docIds[0] }) : null
  }

  const isCurrentPath =
    (link.includes('/deals/') && location.pathname.includes('/deals/')) ||
    (link.includes('/people/contact') && location.pathname.includes('/people/contact/')) ||
    (link.includes('/people/companies') && location.pathname.includes('/people/companies/'))

  return link && !isCurrentPath ? (
    <Link to={link} classes={{ root: linkClass }} onClick={handleLinkClick}>
      <Activity {...props} classes={classes} />
    </Link>
  ) : (
    <Activity {...props} classes={classes} />
  )
}
